/**
 * Presentation Layer - useEditDialog Hook
 * データ編集ダイアログのためのフック
 */

import { useState, useCallback } from 'react';
import {
  EditableField,
  STATUS_OPTIONS,
  DOCTOR_OPTIONS,
  MANUAL_INPUT_VALUE,
} from '../../../../domain/medicalReservation/EditableFieldsPolicy';

/**
 * ダイアログ状態の型定義
 */
export interface EditDialogState {
  /** ダイアログが開いているか */
  isOpen: boolean;
  /** 編集対象のフィールド */
  field: EditableField | null;
  /** 現在の値 */
  currentValue: string;
  /** 対象の行インデックス */
  rowIndex: number | null;
  /** 手動入力モードか */
  isManualInput: boolean;
  /** 手動入力の値 */
  manualInputValue: string;
  /** 選択肢 */
  options: readonly { value: string; label: string }[];
}

/**
 * ダイアログアクションの型定義
 */
export interface EditDialogActions {
  /** ダイアログを開く */
  openDialog: (rowIndex: number, field: EditableField, currentValue: string) => void;
  /** ダイアログを閉じる */
  closeDialog: () => void;
  /** 値を選択 */
  selectValue: (value: string) => void;
  /** 手動入力モードに切り替え */
  enableManualInput: () => void;
  /** 手動入力値を更新 */
  setManualInputValue: (value: string) => void;
  /** 保存を実行 */
  saveEdit: (onSave: (rowIndex: number, field: string, value: string) => void) => void;
}

/**
 * フックの戻り値型
 */
export type UseEditDialogReturn = [EditDialogState, EditDialogActions];

/**
 * 初期状態
 */
const initialState: EditDialogState = {
  isOpen: false,
  field: null,
  currentValue: '',
  rowIndex: null,
  isManualInput: false,
  manualInputValue: '',
  options: [],
};

/**
 * フィールドに応じた選択肢を取得
 */
const getOptionsForField = (
  field: EditableField
): readonly { value: string; label: string }[] => {
  switch (field) {
    case EditableField.STATUS:
      return STATUS_OPTIONS;
    case EditableField.DOCTOR:
      return DOCTOR_OPTIONS;
    default:
      return [];
  }
};

/**
 * データ編集ダイアログフック
 */
export const useEditDialog = (): UseEditDialogReturn => {
  const [state, setState] = useState<EditDialogState>(initialState);

  /**
   * ダイアログを開く
   */
  const openDialog = useCallback(
    (rowIndex: number, field: EditableField, currentValue: string) => {
      const options = getOptionsForField(field);

      // 現在の値が選択肢にない場合は手動入力モードで開始
      const isExistingOption = options.some(
        (opt) => opt.value === currentValue || opt.label === currentValue
      );
      const isManualInput = currentValue !== '' && !isExistingOption;

      setState({
        isOpen: true,
        field,
        currentValue,
        rowIndex,
        isManualInput,
        manualInputValue: isManualInput ? currentValue : '',
        options,
      });
    },
    []
  );

  /**
   * ダイアログを閉じる
   */
  const closeDialog = useCallback(() => {
    setState(initialState);
  }, []);

  /**
   * 値を選択
   */
  const selectValue = useCallback((value: string) => {
    if (value === MANUAL_INPUT_VALUE) {
      // 手動入力モードに切り替え
      setState((prev) => ({
        ...prev,
        isManualInput: true,
        currentValue: '',
        manualInputValue: '',
      }));
    } else {
      setState((prev) => ({
        ...prev,
        currentValue: value,
        isManualInput: false,
        manualInputValue: '',
      }));
    }
  }, []);

  /**
   * 手動入力モードに切り替え
   */
  const enableManualInput = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isManualInput: true,
      manualInputValue: prev.currentValue,
    }));
  }, []);

  /**
   * 手動入力値を更新
   */
  const setManualInputValue = useCallback((value: string) => {
    setState((prev) => ({
      ...prev,
      manualInputValue: value,
    }));
  }, []);

  /**
   * 保存を実行
   */
  const saveEdit = useCallback(
    (onSave: (rowIndex: number, field: string, value: string) => void) => {
      const { rowIndex, field, currentValue, isManualInput, manualInputValue } =
        state;

      if (rowIndex === null || field === null) {
        return;
      }

      // 保存する値を決定
      const valueToSave = isManualInput ? manualInputValue : currentValue;

      if (valueToSave.trim() === '') {
        return;
      }

      // コールバックを呼び出し
      onSave(rowIndex, field, valueToSave);

      // ダイアログを閉じる
      setState(initialState);
    },
    [state]
  );

  // アクションオブジェクト
  const actions: EditDialogActions = {
    openDialog,
    closeDialog,
    selectValue,
    enableManualInput,
    setManualInputValue,
    saveEdit,
  };

  return [state, actions];
};
